import { useCallback, useEffect, useState } from 'react';
import { Pagination, PaginationItem, Stack } from '@mui/material';
import styles from './ATMHistory.module.css';
import {
  atmActionHistoryService,
  AtmActionHistoryEntry,
  AtmActionHistoryFilters,
  AtmActionHistoryPageInfo,
  AtmActionHistorySummary,
  AtmActionType,
  ATM_ACTION_TYPES,
} from '../../services/atmActionHistory.service';
import { getAtmProviders, AtmProvider } from '../../services/atmTerminals.service';

const PAGE_SIZE = 25;
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_RANGE_DAYS = 7;

const ACTION_LABELS: Record<AtmActionType, string> = {
  cash_in: 'Cash In',
  cash_out: 'Cash Out',
  registration: 'Registration',
};

const formatDate = (dateStr?: string | null) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Registration rows carry no amount at all, so a dash is the honest reading —
// never $0.00, which would look like a zero-value transaction.
const formatAmount = (amount?: number | null) =>
  amount == null ? '—' : `$${Number(amount).toFixed(2)}`;

// Summary totals are always present, unlike a row's nullable amount, so these
// never render the em-dash that formatAmount uses.
const formatTotal = (v: number) => `$${Math.abs(v).toFixed(2)}`;
const formatProfit = (v: number) => `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;

const actionClass = (action: AtmActionType): string => {
  switch (action) {
    case 'cash_in':
      return styles.actionCashIn;
    case 'cash_out':
      return styles.actionCashOut;
    case 'registration':
      return styles.actionRegistration;
    default:
      return styles.actionDefault;
  }
};

/**
 * The player on a row comes from one of two columns: `user` for internal
 * providers (a platform account) and `atmExternalUser` for terminal providers,
 * where the account only exists provider-side. Exactly one is set.
 */
const playerOf = (
  entry: AtmActionHistoryEntry,
): { username: string; kind: string } | null => {
  if (entry.user) return { username: entry.user.username, kind: 'Platform user' };
  if (entry.atmExternalUser) {
    return { username: entry.atmExternalUser.username, kind: 'Provider account' };
  }
  return null;
};

// `<input type="date">` gives a bare day; the backend filters on a DateTime, so
// widen each end to cover the whole local day rather than midnight UTC.
const startOfDay = (day: string): string => new Date(`${day}T00:00:00`).toISOString();
const endOfDay = (day: string): string => new Date(`${day}T23:59:59.999`).toISOString();

// `<input type="date">` wants a local YYYY-MM-DD, not an ISO instant.
const asDayInput = (d: Date): string =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const ATMHistory = () => {
  const [items, setItems] = useState<AtmActionHistoryEntry[]>([]);
  const [pageInfo, setPageInfo] = useState<AtmActionHistoryPageInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [providers, setProviders] = useState<AtmProvider[]>([]);
  const [filterProviderId, setFilterProviderId] = useState<number | ''>('');
  const [filterAction, setFilterAction] = useState<AtmActionType | ''>('');
  const [filterUsername, setFilterUsername] = useState('');
  const [filterFrom, setFilterFrom] = useState(() =>
    asDayInput(new Date(Date.now() - DEFAULT_RANGE_DAYS * DAY_MS)),
  );
  const [filterTo, setFilterTo] = useState(() => asDayInput(new Date()));
  const [summary, setSummary] = useState<AtmActionHistorySummary | null>(null);

  // Detail panel. The row already carries every field, but it is re-fetched by
  // id so an open panel shows the record as it is now, not as the page was.
  const [selected, setSelected] = useState<AtmActionHistoryEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    getAtmProviders()
      .then(setProviders)
      .catch(() => {
        // A provider list we can't load only costs the dropdown; the log itself
        // still works unfiltered.
      });
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: AtmActionHistoryFilters = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (filterProviderId) filters.providerId = Number(filterProviderId);
      if (filterAction) filters.action = filterAction;
      if (filterUsername.trim()) filters.username = filterUsername.trim();
      if (filterFrom) filters.from = startOfDay(filterFrom);
      if (filterTo) filters.to = endOfDay(filterTo);

      const data = await atmActionHistoryService.getHistory(filters);
      setItems(data.items);
      setPageInfo(data.pageInfo);
      setSummary(data.summary);
    } catch (err: any) {
      setError(err?.message || 'Failed to load ATM history');
      setItems([]);
      setPageInfo(null);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [page, filterProviderId, filterAction, filterUsername, filterFrom, filterTo]);

  // Selects and dates apply immediately; the username box waits for Search so
  // it isn't querying on every keystroke.
  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterProviderId, filterAction, filterFrom, filterTo]);

  const triggerSearch = () => {
    if (page === 0) {
      fetchHistory();
    } else {
      setPage(1); // the effect refetches
    }
  };

  const handleClear = () => {
    setFilterProviderId('');
    setFilterAction('');
    setFilterUsername('');
    setFilterFrom(asDayInput(new Date(Date.now() - DEFAULT_RANGE_DAYS * DAY_MS)));
    setFilterTo(asDayInput(new Date()));
    setPage(1);
  };

  const openEntry = async (entry: AtmActionHistoryEntry) => {
    setSelected(entry);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const fresh = await atmActionHistoryService.getEntry(entry.id);
      if (fresh) setSelected(fresh);
      else setDetailError('This record no longer exists.');
    } catch (err: any) {
      setDetailError(err?.message || 'Failed to load this record');
    } finally {
      setDetailLoading(false);
    }
  };

  const closePanel = () => {
    setSelected(null);
    setDetailError(null);
  };

  const player = selected ? playerOf(selected) : null;

  return (
    <main className={styles.main}>
      <div
        className={`${styles.overlay} ${selected ? styles.open : ''}`}
        onClick={closePanel}
      />

      <div className={styles.headerRow}>
        <h1 className={styles.title}>ATM History</h1>
        {pageInfo && (
          <span className={styles.totalBadge}>
            {pageInfo.total.toLocaleString('en-US')} records
          </span>
        )}
      </div>

      {summary && (
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Cash In</span>
            <span className={`${styles.summaryValue} ${styles.cashIn}`}>
              {formatTotal(summary.totalCashIn)}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Cash Out</span>
            <span className={`${styles.summaryValue} ${styles.cashOut}`}>
              {formatTotal(summary.totalCashOut)}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Profit</span>
            <span
              className={`${styles.summaryValue} ${
                summary.profit < 0 ? styles.cashOut : ''
              }`}
            >
              {formatProfit(summary.profit)}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Registrations</span>
            <span className={styles.summaryValue}>
              {summary.totalRegistrations.toLocaleString('en-US')}
            </span>
          </div>
        </div>
      )}

      <div className={styles.filtersRow}>
        <select
          className={styles.filterSelect}
          value={filterProviderId}
          onChange={(e) => {
            setFilterProviderId(e.target.value ? Number(e.target.value) : '');
            setPage(1);
          }}
        >
          <option value="">All Providers</option>
          {providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.name}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={filterAction}
          onChange={(e) => {
            setFilterAction((e.target.value as AtmActionType) || '');
            setPage(1);
          }}
        >
          <option value="">All Actions</option>
          {ATM_ACTION_TYPES.map((action) => (
            <option key={action} value={action}>
              {ACTION_LABELS[action]}
            </option>
          ))}
        </select>

        <input
          className={styles.filterInput}
          type="text"
          placeholder="Username..."
          value={filterUsername}
          onChange={(e) => setFilterUsername(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') triggerSearch();
          }}
        />

        <label className={styles.dateField}>
          <span className={styles.dateLabel}>From</span>
          <input
            className={styles.filterInput}
            type="date"
            value={filterFrom}
            max={filterTo || undefined}
            onChange={(e) => {
              setFilterFrom(e.target.value);
              setPage(1);
            }}
          />
        </label>

        <label className={styles.dateField}>
          <span className={styles.dateLabel}>To</span>
          <input
            className={styles.filterInput}
            type="date"
            value={filterTo}
            min={filterFrom || undefined}
            onChange={(e) => {
              setFilterTo(e.target.value);
              setPage(1);
            }}
          />
        </label>

        <button className={styles.filterBtn} onClick={triggerSearch}>
          Search
        </button>
        <button className={styles.filterClearBtn} onClick={handleClear}>
          Clear
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading ATM history...</div>
      ) : items.length === 0 ? (
        <div className={styles.noData}>No ATM actions found</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Amount</th>
                  <th>Player</th>
                  <th>ATM</th>
                  <th>Provider</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => {
                  const rowPlayer = playerOf(entry);
                  return (
                    <tr
                      key={entry.id}
                      onClick={() => openEntry(entry)}
                      className={selected?.id === entry.id ? styles.selectedRow : ''}
                    >
                      <td>{entry.id}</td>
                      <td>{formatDate(entry.createdAt)}</td>
                      <td>
                        <span className={`${styles.badge} ${actionClass(entry.action)}`}>
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </span>
                      </td>
                      <td className={styles.amount}>{formatAmount(entry.amount)}</td>
                      <td>
                        {rowPlayer ? (
                          <>
                            {rowPlayer.username}
                            <span className={styles.cellHint}>{rowPlayer.kind}</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{entry.atm?.username ?? '—'}</td>
                      <td>{entry.atmProvider?.name ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageInfo && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {pageInfo.total === 0 ? 0 : pageInfo.offset + 1}–
                {pageInfo.offset + items.length} of {pageInfo.total}
              </div>
              <div className={styles.paginationControls}>
                <Stack spacing={1}>
                  <Pagination
                    count={Math.max(1, Math.ceil(pageInfo.total / PAGE_SIZE))}
                    page={page}
                    onChange={(_e, value) => setPage(value)}
                    color="primary"
                    size="medium"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: '#313485',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#232665' },
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
              </div>
            </div>
          )}
        </>
      )}

      <div className={`${styles.detailPanel} ${selected ? styles.open : ''}`}>
        {selected && (
          <>
            <div className={styles.detailPanelHeader}>
              <h2 className={styles.detailTitle}>Action #{selected.id}</h2>
              <button className={styles.closeBtn} onClick={closePanel}>
                ✕
              </button>
            </div>

            <div className={styles.detailBody}>
              {detailError && <div className={styles.error}>{detailError}</div>}
              {detailLoading && <div className={styles.detailLoading}>Refreshing…</div>}

              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Action</div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Type</span>
                    <span className={styles.infoValue}>
                      <span className={`${styles.badge} ${actionClass(selected.action)}`}>
                        {ACTION_LABELS[selected.action] ?? selected.action}
                      </span>
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Amount</span>
                    <span className={styles.infoValue}>{formatAmount(selected.amount)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Date</span>
                    <span className={styles.infoValue}>{formatDate(selected.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Player</div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Username</span>
                    <span className={styles.infoValue}>{player?.username ?? '—'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Account type</span>
                    <span className={styles.infoValue}>{player?.kind ?? '—'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>User ID</span>
                    <span className={styles.infoValue}>
                      {selected.userId ?? selected.atmExternalUserId ?? '—'}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Terminal</div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>ATM</span>
                    <span className={styles.infoValue}>{selected.atm?.username ?? '—'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>ATM ID</span>
                    <span className={styles.infoValue}>{selected.atmId}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Provider</span>
                    <span className={styles.infoValue}>
                      {selected.atmProvider?.name ?? '—'}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Provider ID</span>
                    <span className={styles.infoValue}>{selected.atmProviderId}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ATMHistory;
